/**
 * web/src/screens/Life/Decisions.tsx — the Decisions rail (parent card
 * VcBi3nr9GjBj, child C bwcTJh8Od5rk; composer fix tlqYJpZHrLVc).
 *
 * Life > Requests carries two rails: Requests (the asks) and Decisions (the
 * couple-authored two-option choices). This file is the second one. It draws
 * the SAME sealed payload the Thread card uses — /api/decisions is already
 * projected for the caller — so there is no second source of truth for what a
 * partner picked, and no place for the seal to leak.
 *
 * Two ways to start a decision, and only one of them is about time:
 *   - the composer (card tlqYJpZHrLVc): the author types the question and the
 *     two options. This is the ordinary route — a decision is two things you
 *     choose between, not two dates.
 *   - the "Pick a time" tile is the needs_time route (parent AC6): both
 *     candidate times are prefilled from shared/decisions.dateTimeCandidates,
 *     so a person chooses a time instead of typing one, and the decision
 *     records initiation_source=date_needs_time. It never stands in for the
 *     question body.
 */
import { useCallback, useEffect, useState } from 'react';
import { createDecision, listDecisions, voteDecision } from '../../lib/api';
import {
  DECISION_VERBS,
  dateTimeCandidates,
  selectDecisionRail,
  type DecisionChoice,
  type DecisionView,
} from '../../../../shared/decisions';

/** Option text is capped at the same 60 chars parseDecisionIntent accepts, so
 *  a hand-typed card and an armed message agree on what an option may be. */
const OPTION_MAX = 60;
const TITLE_MAX = 120;

function stateLine(v: DecisionView, nameFor?: (id: string) => string): string {
  if (v.state === 'tied') return 'You picked differently — it is on the Plan agenda.';
  if (v.state === 'revealed') return v.outcomeLine ?? 'Both of you picked.';
  if (v.stalled) return 'Still undecided — it is on the Plan agenda.';
  const voted = v.votes.map((x: { mine: boolean; memberId: string }) => (x.mine ? 'you' : nameFor?.(x.memberId) ?? 'your partner'));
  return voted.length === 0 ? 'Neither of you has picked yet.' : `${voted.join(' and ')} picked — the other is still open.`;
}

export function DecisionsRail({ nameFor }: { nameFor?: (id: string) => string }) {
  const [views, setViews] = useState<DecisionView[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* Composer (card tlqYJpZHrLVc). The question and both options are typed by
     the author — a decision the couple actually has in mind, not two slots
     the clock offered them. */
  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState('');
  const [optionA, setOptionA] = useState('');
  const [optionB, setOptionB] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await listDecisions();
      setViews((data.decisions ?? []) as DecisionView[]);
    } catch {
      setViews([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* A decision moves on either device, and the room broadcasts an id-only
     frame on every create/vote/settle — same hand-off as chores. */
  useEffect(() => {
    const reload = () => { void load(); };
    window.addEventListener('pear:decisions', reload);
    window.addEventListener('focus', reload);
    return () => {
      window.removeEventListener('pear:decisions', reload);
      window.removeEventListener('focus', reload);
    };
  }, [load]);

  const pick = async (id: string, choice: DecisionChoice) => {
    if (busy) return;
    setBusy(id);
    setError(null);
    try {
      await voteDecision(id, choice);
      await load();
    } catch {
      setError('That pick did not save. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const resetComposer = () => {
    setComposing(false);
    setTitle('');
    setOptionA('');
    setOptionB('');
    setFormError(null);
  };

  /** The ordinary route: the author's own question and their own two options. */
  const createFromComposer = async () => {
    if (busy) return;
    const t = title.trim();
    const a = optionA.trim();
    const b = optionB.trim();
    if (!t) return setFormError('Give the decision a question.');
    if (!a || !b) return setFormError('Both options are needed — that is what makes it a choice.');
    if (a.toLowerCase() === b.toLowerCase()) return setFormError('The two options need to be different.');
    if (a.length > OPTION_MAX || b.length > OPTION_MAX) return setFormError(`Keep each option under ${OPTION_MAX} characters.`);
    if (t.length > TITLE_MAX) return setFormError(`Keep the question under ${TITLE_MAX} characters.`);
    setBusy('new');
    setError(null);
    setFormError(null);
    try {
      await createDecision({
        title: t,
        optionA: a,
        optionB: b,
        initiationSource: 'composer',
      });
      resetComposer();
      await load();
    } catch {
      setFormError('That decision did not save. Try again.');
    } finally {
      setBusy(null);
    }
  };

  /** The needs_time route: two candidate times, prefilled, no typing. */
  const pickATime = async () => {
    if (busy) return;
    setBusy('new');
    setError(null);
    try {
      const { a, b } = dateTimeCandidates();
      await createDecision({
        title: 'When shall we go?',
        optionA: a,
        optionB: b,
        initiationSource: 'date_needs_time',
      });
      await load();
    } catch {
      setError('That did not save. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const composer = composing ? (
    <form
      className="decisions-rail-composer"
      data-decisions-composer=""
      onSubmit={(e) => { e.preventDefault(); void createFromComposer(); }}
    >
      <label className="decisions-rail-field">
        <span>What are you deciding?</span>
        <input
          type="text"
          value={title}
          placeholder="Dinner on Friday"
          maxLength={TITLE_MAX}
          data-decisions-title=""
          autoFocus
          onChange={(e) => { setTitle(e.target.value); setFormError(null); }}
        />
      </label>
      <label className="decisions-rail-field">
        <span>Option A</span>
        <input
          type="text"
          value={optionA}
          placeholder="Thai place"
          maxLength={OPTION_MAX}
          data-decisions-option-a=""
          onChange={(e) => { setOptionA(e.target.value); setFormError(null); }}
        />
      </label>
      <label className="decisions-rail-field">
        <span>Option B</span>
        <input
          type="text"
          value={optionB}
          placeholder="Cook at home"
          maxLength={OPTION_MAX}
          data-decisions-option-b=""
          onChange={(e) => { setOptionB(e.target.value); setFormError(null); }}
        />
      </label>
      {formError && <p className="decisions-rail-error" role="alert">{formError}</p>}
      <div className="decisions-rail-actions">
        <button type="submit" className="pear-btn pear-btn-mustard" data-decisions-create="" disabled={busy !== null}>
          Ask your partner
        </button>
        <button type="button" className="pear-btn" data-decisions-cancel="" disabled={busy !== null} onClick={resetComposer}>
          Cancel
        </button>
      </div>
    </form>
  ) : null;

  const newDecisionButton = (
    <div className="decisions-rail-start">
      <button
        type="button"
        className="decisions-rail-new"
        data-decisions-new=""
        disabled={busy !== null}
        onClick={() => { setComposing(true); setFormError(null); }}
      >
        Ask a decision
      </button>
      <button
        type="button"
        className="decisions-rail-new decisions-rail-new-time"
        data-decisions-pick-time=""
        disabled={busy !== null}
        onClick={() => void pickATime()}
      >
        Pick a date and time together
      </button>
    </div>
  );

  if (!views) return null;

  const { open, resolved } = selectDecisionRail(views);
  if (views.length === 0 && !composing) {
    return (
      <section className="decisions-rail" data-decisions-rail="" aria-label="Decisions">
        {newDecisionButton}
      </section>
    );
  }

  return (
    <section className="decisions-rail" data-decisions-rail="" aria-label="Decisions">
      <h2 className="section-title decisions-rail-title">Decisions</h2>
      {error && <p className="decisions-rail-error" role="alert">{error}</p>}
      {composer}
      {[...open, ...resolved].length > 0 && (
        <ul className="decisions-rail-list">
          {[...open, ...resolved].map(v => (
            <li className="decisions-rail-item" key={v.decisionId} data-decision-id={v.decisionId} data-decisions-state={v.state}>
              <div className="decisions-rail-main">
                <span className="decisions-rail-question">{v.title}</span>
                <span className="decisions-rail-options">
                  <b>{v.options.a}</b> or <b>{v.options.b}</b>
                  {v.extraOptions.length > 0 && <> or <b>{v.extraOptions.join(' or ')}</b></>}
                </span>
                <span className="decisions-rail-state">{stateLine(v, nameFor)}</span>
              </div>
              {v.state === 'pending' && (
                <div className="decisions-rail-actions">
                  <button
                    type="button"
                    className="pear-btn pear-btn-mustard"
                    data-decisions-lock-in=""
                    disabled={busy !== null}
                    onClick={() => void pick(v.decisionId, 'a')}
                  >
                    {DECISION_VERBS.a}
                  </button>
                  <button
                    type="button"
                    className="pear-btn"
                    data-decisions-either=""
                    disabled={busy !== null}
                    onClick={() => void pick(v.decisionId, 'b')}
                  >
                    {DECISION_VERBS.b}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {!composing && newDecisionButton}
    </section>
  );
}
